/**
 * LanguageSwitcher — Globe-Icon im Header öffnet ein Dropdown mit
 * allen unterstützten Sprachen. Persistiert in LocalStorage über i18next.
 */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LANGUAGES, RTL_LANGUAGES } from "@/i18n";

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage)
                || SUPPORTED_LANGUAGES[0];

  // Synchronisiere <html lang="…"> und <html dir="rtl|ltr"> mit der UI-Sprache.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = current.code;
      document.documentElement.dir = RTL_LANGUAGES.has(current.code) ? "rtl" : "ltr";
    }
  }, [current.code]);

  const change = (code) => {
    i18n.changeLanguage(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="group flex items-center gap-1.5 h-9 px-2.5 rounded-full border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm transition"
          title={t("lang_switcher.label")}
          data-testid="language-switcher-trigger"
          aria-label={t("lang_switcher.label")}
        >
          <Globe className="h-4 w-4 text-zinc-500 group-hover:text-zinc-700" />
          <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
          <span className="hidden md:inline text-[11px] uppercase tracking-[0.16em] font-bold text-zinc-600">
            {current.code}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="language-switcher-content">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">
          {t("lang_switcher.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => change(lang.code)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`lang-option-${lang.code}`}
          >
            <span className="flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden="true">{lang.flag}</span>
              <span className="text-sm">{lang.label}</span>
              <span className="text-[10px] font-mono text-zinc-400 uppercase">{lang.code}</span>
            </span>
            {lang.code === current.code && (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
